/**
 * Round Management Component
 * 
 * Main component for managing game rounds in the admin dashboard.
 * Delegates to game-type-specific components for HNS and FPR.
 * 
 * Architecture:
 * - RoundManagement: Generic container, game selection, round creation
 * - HnsRoundCard: Hide and Seek specific round display and actions
 * - FprRoundCard: FP Race specific round display and actions
 */

import { Fragment, useState, useCallback, useMemo } from 'react';
import {
  hnstCoinTossLabel,
  hnstTossRoundIndexes,
  type HnstCoinToss,
  isHnsSettings,
  isHnstSettings,
  isHideSeekSettings,
  isFprSettings,
  isTrlSettings,
  isHnsRoundPlayer,
  isRoundActive,
  GameType,
  getGameMetadata,
  canAdminDo,
  resolveHideSeekRoundDefaults,
} from '@swtorbit/shared-socket-events';
import { getEnabledPlayers } from '@swtorbit/shared-react-hooks';
import { Modal } from '@swtorbit/ui/react';
import { useGameContext } from '../contexts/GameContext';
import { HnsRoundCard, HnsRoundSettingsForm } from './hns';
import { HnsLocationPlanPanel } from './hns/HnsLocationPlanPanel';
import { HnsRoundDefaultsPanel } from './hns/HnsRoundDefaultsPanel';
import { RulesPanel } from './rules/RulesPanel';
import { HnstCoinTossCard, HnstCoinTossForm } from './hnst';
import { FprRoundCard, FprRoundSettingsForm } from './fpr';
import { TrlRoundCard, TrlRoundSettingsForm, TrlMapBuilder } from './trl';

export function RoundManagement() {
  const { selectedGame, createRound, startRound, endRound, cancelRound, disableRound, forceResults, editRound, loadRound, unloadRound, deleteRound, updateRoundPlayer } = useGameContext();
  const [isCreatingRound, setIsCreatingRound] = useState(false);
  
  // Get only enabled players (disabled players are hidden from round management)
  const enabledPlayers = useMemo(() => getEnabledPlayers(selectedGame), [selectedGame?.players]);

  // FPR: detect if a GRAND FINAL round has already been created
  const hasFinalRound = useMemo(() => {
    if (!selectedGame) return false;
    return selectedGame.rounds.some(
      (r) => isFprSettings(r.settings) && r.settings.isFinal === true
    );
  }, [selectedGame?.rounds]);

  const teamCount = selectedGame?.fprTeams?.length ?? 0;

  // Round editing state - just track which round is being edited
  const [editingRoundIndex, setEditingRoundIndex] = useState<number | null>(null);

  // HNST: the coin toss being added (toss null) or changed.
  const [tossForm, setTossForm] = useState<{ toss: HnstCoinToss | null } | null>(null);
  /** HNST: the coin tosses shown just above a round: those it is the first round of (null: those with no rounds). */
  const tossesAt = useCallback((roundIndex: number | null): HnstCoinToss[] => {
    if (!selectedGame) return [];
    return (selectedGame.hnstCoinTosses ?? []).filter((toss) => (hnstTossRoundIndexes(selectedGame, toss.id)[0] ?? null) === roundIndex);
  }, [selectedGame]);

  const handleStartRound = useCallback((roundIndex: number) => {
    if (!selectedGame) return;
    startRound({
      roomCode: selectedGame.roomCode,
      roundIndex,
    });
  }, [selectedGame, startRound]);

  const handleEndRound = useCallback((roundIndex: number) => {
    if (!selectedGame) return;
    endRound({
      roomCode: selectedGame.roomCode,
      roundIndex,
    });
  }, [selectedGame, endRound]);

  const handleCancelRound = useCallback((roundIndex: number) => {
    if (!selectedGame) return;
    cancelRound({
      roomCode: selectedGame.roomCode,
      roundIndex,
    });
  }, [selectedGame, cancelRound]);

  const handleDisableRound = useCallback((roundIndex: number) => {
    if (!selectedGame) return;
    disableRound({
      roomCode: selectedGame.roomCode,
      roundIndex,
    });
  }, [selectedGame, disableRound]);

  const handleForceResults = useCallback((roundIndex: number) => {
    if (!selectedGame) return;
    forceResults({
      roomCode: selectedGame.roomCode,
      roundIndex,
    });
  }, [selectedGame, forceResults]);

  const handleTogglePlayerRole = useCallback((roundIndex: number, playerId: string, currentRole: 'hider' | 'seeker') => {
    if (!selectedGame) return;
    const newRole = currentRole === 'hider' ? 'seeker' : 'hider';
    updateRoundPlayer({
      roomCode: selectedGame.roomCode,
      roundIndex,
      playerId,
      role: newRole,
    });
  }, [selectedGame, updateRoundPlayer]);

  const handleLoadRound = useCallback((roundIndex: number) => {
    if (!selectedGame) return;
    loadRound({
      roomCode: selectedGame.roomCode,
      roundIndex,
    });
  }, [selectedGame, loadRound]);

  const handleUnloadRound = useCallback((roundIndex: number) => {
    if (!selectedGame) return;
    unloadRound({
      roomCode: selectedGame.roomCode,
      roundIndex,
    });
  }, [selectedGame, unloadRound]);

  const handleDeleteRound = useCallback((roundIndex: number) => {
    if (!selectedGame) return;
    if (!window.confirm(`Delete Round ${roundIndex + 1}? This cannot be undone.`)) return;
    deleteRound({
      roomCode: selectedGame.roomCode,
      roundIndex,
    });
  }, [selectedGame, deleteRound]);

  // Open edit form for a round
  const handleOpenEditRound = useCallback((roundIndex: number) => {
    if (!selectedGame) return;
    const round = selectedGame.rounds[roundIndex];
    if (!round) return;
    setEditingRoundIndex(roundIndex);
  }, [selectedGame]);

  // Cancel editing
  const handleCancelEditRound = useCallback(() => {
    setEditingRoundIndex(null);
  }, []);

  // No game selected - show message
  if (!selectedGame) {
    return (
      <div className="w-100 align-self-stretch text-white">
        <h2>Round Management</h2>
        <div className="alert">
          <p>Select a game from the header to manage rounds.</p>
        </div>
      </div>
    );
  }

  // Get game type info
  const gameMetadata = selectedGame ? getGameMetadata(selectedGame.gameType) : null;
  const isHnsGame = selectedGame?.gameType === GameType.HIDE_AND_SEEK;
  const isFprGame = selectedGame?.gameType === GameType.FP_RACE;
  const isTrlGame = selectedGame?.gameType === GameType.TRAILBLAZERS;
  const isHnstGame = selectedGame?.gameType === GameType.HIDE_AND_SEEK_TEAMS;

  // Trailblazers is authored as a galaxy map (node graph), not a flat round
  // list — it gets its own builder instead of the create/edit/round-card UI.
  // Start Game and the per-team start-selection status live in the lifecycle
  // modal and the Teams tab respectively — not here.
  if (isTrlGame) {
    return (
      <div className="w-100 align-self-stretch text-white">
        <h2>Galaxy Map</h2>
        <TrlMapBuilder
          roomCode={selectedGame.roomCode}
          readOnly={!canAdminDo(GameType.TRAILBLAZERS, selectedGame.status, 'canEditMap')}
        />
      </div>
    );
  }

  return (
    <div className="w-100 align-self-stretch text-white">
      <h2>Round Management</h2>
      <p className="game-subtitle">
        {gameMetadata?.icon} {selectedGame.name} ({selectedGame.roomCode}) • {enabledPlayers.length} player{enabledPlayers.length !== 1 ? 's' : ''}{enabledPlayers.length !== selectedGame.players.length ? ` (${selectedGame.players.length - enabledPlayers.length} disabled)` : ''} • {selectedGame.rounds.length} round{selectedGame.rounds.length !== 1 ? 's' : ''}
      </p>

          {(isHnsGame || isHnstGame) && <RulesPanel game={selectedGame} />}
          {(isHnsGame || isHnstGame) && <HnsRoundDefaultsPanel game={selectedGame} />}
          {(isHnsGame || isHnstGame) && <HnsLocationPlanPanel game={selectedGame} />}

          {/* Create Round Button */}
          <button
            onClick={() => setIsCreatingRound(true)}
            className="btn btn--green btn--lg"
            disabled={hasFinalRound}
            title={hasFinalRound ? 'A GRAND FINAL round has been created — no further rounds can be added.' : undefined}
          >
            + Create New Round
          </button>
          {isHnstGame && (
            <button onClick={() => setTossForm({ toss: null })} className="btn btn--amber btn--lg ml-xs">
              🪙 Add Coin Toss
            </button>
          )}
          {hasFinalRound && (
            <p className="field__hint fs-xs">
              🏆 GRAND FINAL round is set — no further rounds can be created.
            </p>
          )}

          {isHnstGame && tossForm && (
            <Modal open={true} onClose={() => setTossForm(null)} title={tossForm.toss ? `Edit ${hnstCoinTossLabel(selectedGame, tossForm.toss.id)}` : 'Add Coin Toss'}>
              <HnstCoinTossForm game={selectedGame} toss={tossForm.toss} onDone={() => setTossForm(null)} />
            </Modal>
          )}

          {/* Create Round Modal - Game Type Aware */}
          <Modal open={isCreatingRound} onClose={() => setIsCreatingRound(false)} className="round-creation-modal" title="Create New Round">
              {isHnsGame && (
                <HnsRoundSettingsForm
                  players={enabledPlayers}
                  rounds={selectedGame.rounds}
                  locationPlan={selectedGame.hnsLocationPlan}
                  roundDefaults={resolveHideSeekRoundDefaults(GameType.HIDE_AND_SEEK, selectedGame.hnsRoundDefaults)}
                  onSubmit={(settings, players, initialRoundEvents) => {
                    createRound({
                      roomCode: selectedGame.roomCode,
                      settings,
                      players,
                      ...(initialRoundEvents.length > 0 ? { initialRoundEvents } : {}),
                    });
                    setIsCreatingRound(false);
                  }}
                  onCancel={() => setIsCreatingRound(false)}
                />
              )}
              
              {isHnstGame && (
                <HnsRoundSettingsForm
                  mode="hnst"
                  players={enabledPlayers}
                  rounds={selectedGame.rounds}
                  locationPlan={selectedGame.hnsLocationPlan}
                  roundDefaults={resolveHideSeekRoundDefaults(GameType.HIDE_AND_SEEK_TEAMS, selectedGame.hnsRoundDefaults)}
                  onSubmit={(settings, players, initialRoundEvents) => {
                    createRound({
                      roomCode: selectedGame.roomCode,
                      settings,
                      players,
                      ...(initialRoundEvents.length > 0 ? { initialRoundEvents } : {}),
                    });
                    setIsCreatingRound(false);
                  }}
                  onCancel={() => setIsCreatingRound(false)}
                />
              )}

              {isFprGame && (
                <FprRoundSettingsForm
                  players={enabledPlayers}
                  teamCount={teamCount}
                  onSubmit={(settings, players) => {
                    createRound({
                      roomCode: selectedGame.roomCode,
                      settings,
                      players,
                    });
                    setIsCreatingRound(false);
                  }}
                  onCancel={() => setIsCreatingRound(false)}
                />
              )}

              {isTrlGame && (
                <TrlRoundSettingsForm
                  players={enabledPlayers}
                  onSubmit={(settings, players) => {
                    createRound({
                      roomCode: selectedGame.roomCode,
                      settings,
                      players,
                    });
                    setIsCreatingRound(false);
                  }}
                  onCancel={() => setIsCreatingRound(false)}
                />
              )}
            </Modal>

          {/* Rounds List */}
          <div>
            <h3>Rounds ({selectedGame.rounds.length})</h3>
            {/* A coin toss with no rounds left sits at the top; the others above their first round. */}
            {tossesAt(null).map((toss) => (
              <HnstCoinTossCard key={`toss-${toss.id}`} game={selectedGame} toss={toss} onEdit={(t) => setTossForm({ toss: t })} />
            ))}
            {selectedGame.rounds.length === 0 ? (
              <p>No rounds created yet.</p>
            ) : (
              <div className="rounds-list">
                {selectedGame.rounds.map((round, index) => {
                  const hasActiveRound = selectedGame.rounds.some(isRoundActive);
                  const isLoaded = selectedGame.loadedRoundIndex === index;

                  // Render game-type-specific round card (both Hide and Seek game types)
                  if (isHideSeekSettings(round.settings)) {
                    return (
                      <Fragment key={round.id}>
                      {tossesAt(index).map((toss) => (
                        <HnstCoinTossCard key={`toss-${toss.id}`} game={selectedGame} toss={toss} onEdit={(t) => setTossForm({ toss: t })} />
                      ))}
                      <HnsRoundCard
                        key={round.id}
                        round={round}
                        index={index}
                        hasActiveRound={hasActiveRound}
                        isLoaded={isLoaded}
                        gamePlayers={enabledPlayers}
                        onStart={handleStartRound}
                        onEnd={handleEndRound}
                        onCancel={handleCancelRound}
                        onDisable={handleDisableRound}
                        onDelete={handleDeleteRound}
                        onEdit={handleOpenEditRound}
                        onLoad={handleLoadRound}
                        onUnload={handleUnloadRound}
                        onTogglePlayerRole={handleTogglePlayerRole}
                        onForceResults={handleForceResults}
                      />
                      </Fragment>
                    );
                  }

                  if (isFprSettings(round.settings)) {
                    return (
                      <FprRoundCard
                        key={round.id}
                        round={round}
                        index={index}
                        hasActiveRound={hasActiveRound}
                        isLoaded={isLoaded}
                        gamePlayers={enabledPlayers}
                        onStart={handleStartRound}
                        onEnd={handleEndRound}
                        onCancel={handleCancelRound}
                        onDisable={handleDisableRound}
                        onDelete={handleDeleteRound}
                        onEdit={handleOpenEditRound}
                        onLoad={handleLoadRound}
                        onUnload={handleUnloadRound}
                      />
                    );
                  }

                  if (isTrlSettings(round.settings)) {
                    return (
                      <TrlRoundCard
                        key={round.id}
                        round={round}
                        index={index}
                        hasActiveRound={hasActiveRound}
                        isLoaded={isLoaded}
                        gamePlayers={enabledPlayers}
                        onStart={handleStartRound}
                        onEnd={handleEndRound}
                        onCancel={handleCancelRound}
                        onDisable={handleDisableRound}
                        onDelete={handleDeleteRound}
                        onEdit={handleOpenEditRound}
                        onLoad={handleLoadRound}
                        onUnload={handleUnloadRound}
                      />
                    );
                  }

                  // Fallback for unknown game type
                  return <div key={round.id} className="card card--sm card--bg-midnight card--border card--border-navy">Unknown game type</div>;
                })}
              </div>
            )}
          </div>

          {/* Edit Round Modal - Game Type Aware */}
          {editingRoundIndex !== null && selectedGame.rounds[editingRoundIndex] && (
            <Modal open={true} onClose={handleCancelEditRound} className="round-edit-modal" title={<>Edit Round {editingRoundIndex + 1}</>}>
                {/* HNS Edit Form */}
                {isHnsSettings(selectedGame.rounds[editingRoundIndex].settings) && (() => {
                  const round = selectedGame.rounds[editingRoundIndex];
                  const hnsSettings = isHnsSettings(round.settings) ? round.settings : undefined;
                  if (!hnsSettings) return null;
                  
                  const hnsPlayers = round.players.filter(isHnsRoundPlayer);
                  const seekerIds = new Set(hnsPlayers.filter(p => p.role === 'seeker').map(p => p.playerId));
                  
                  return (
                    <HnsRoundSettingsForm
                      players={enabledPlayers}
                      rounds={selectedGame.rounds}
                      locationPlan={selectedGame.hnsLocationPlan}
                      initialSettings={hnsSettings}
                      initialSeekerIds={seekerIds}
                      initialEvents={round.roundEvents}
                      isEditing={true}
                      roundDefaults={resolveHideSeekRoundDefaults(GameType.HIDE_AND_SEEK, selectedGame.hnsRoundDefaults)}
                      onSubmit={(settings, players, initialRoundEvents) => {
                        // Always sent, so removing every event saves too.
                        editRound({
                          roomCode: selectedGame.roomCode,
                          roundIndex: editingRoundIndex,
                          settings,
                          players,
                          initialRoundEvents,
                        });
                        setEditingRoundIndex(null);
                      }}
                      onCancel={handleCancelEditRound}
                    />
                  );
                })()}

                {/* HNST Edit Form */}
                {isHnstSettings(selectedGame.rounds[editingRoundIndex].settings) && (() => {
                  const round = selectedGame.rounds[editingRoundIndex];
                  if (!isHnstSettings(round.settings)) return null;
                  return (
                    <HnsRoundSettingsForm
                      mode="hnst"
                      players={enabledPlayers}
                      rounds={selectedGame.rounds}
                      locationPlan={selectedGame.hnsLocationPlan}
                      initialSettings={round.settings}
                      initialEvents={round.roundEvents}
                      isEditing={true}
                      coinTossLabel={round.settings.coinTossId != null ? hnstCoinTossLabel(selectedGame, round.settings.coinTossId) : null}
                      roundDefaults={resolveHideSeekRoundDefaults(GameType.HIDE_AND_SEEK_TEAMS, selectedGame.hnsRoundDefaults)}
                      onSubmit={(settings, players, initialRoundEvents) => {
                        // Always sent, so removing every event saves too.
                        editRound({
                          roomCode: selectedGame.roomCode,
                          roundIndex: editingRoundIndex,
                          settings,
                          players,
                          initialRoundEvents,
                        });
                        setEditingRoundIndex(null);
                      }}
                      onCancel={handleCancelEditRound}
                    />
                  );
                })()}

                {/* FPR Edit Form */}
                {isFprSettings(selectedGame.rounds[editingRoundIndex].settings) && (() => {
                  const round = selectedGame.rounds[editingRoundIndex];
                  const fprSettings = isFprSettings(round.settings) ? round.settings : undefined;
                  if (!fprSettings) return null;

                  return (
                    <FprRoundSettingsForm
                      players={enabledPlayers}
                      initialSettings={fprSettings}
                      teamCount={teamCount}
                      isEditing={true}
                      onSubmit={(settings, players) => {
                        editRound({
                          roomCode: selectedGame.roomCode,
                          roundIndex: editingRoundIndex,
                          settings,
                          players,
                        });
                        setEditingRoundIndex(null);
                      }}
                      onCancel={handleCancelEditRound}
                    />
                  );
                })()}

                {/* TRL Edit Form */}
                {isTrlSettings(selectedGame.rounds[editingRoundIndex].settings) && (() => {
                  const round = selectedGame.rounds[editingRoundIndex];
                  const trlSettings = isTrlSettings(round.settings) ? round.settings : undefined;
                  if (!trlSettings) return null;

                  return (
                    <TrlRoundSettingsForm
                      players={enabledPlayers}
                      initialSettings={trlSettings}
                      isEditing={true}
                      onSubmit={(settings, players) => {
                        editRound({
                          roomCode: selectedGame.roomCode,
                          roundIndex: editingRoundIndex,
                          settings,
                          players,
                        });
                        setEditingRoundIndex(null);
                      }}
                      onCancel={handleCancelEditRound}
                    />
                  );
                })()}
              </Modal>
          )}
    </div>
  );
}
